import { action, computed, makeObservable, observable } from 'mobx';

import { DaysRangeData, ShoppingCartData, ShoppingCartItemData, ShoppingCartItemGroupData } from '../../app/data/model';
import { CheckboxInput, Form, TextInput, ValueMaxLength, ValueRequiredRule } from '../../app/shared/Form';
import { CartItemListTranslation } from '../localization/SiteTranslation';
import { CartItemState, ICartItemContextAction } from './CartItem/CartItemState';
import { CartItemGroupState, ICartItemGroupContextAction } from './CartItemGroup/CartItemGroupState';

export class CartState extends Form {
  @computed
  get brandName() {
    if (this.cartItems.length > 0) {
      return this.cart.items[0].baseModel.brandName;
    }

    if (this.cart.groups.length > 0) {
      return this.cart.groups[0].items[0]?.baseModel.brandName;
    }

    return '';
  }

  @computed
  get lineItems() {
    return [...this.cartItems, ...this.cartGroups];
  }

  @observable
  cartItems: CartItemState[] = [];

  @observable
  cartGroups: CartItemGroupState[] = [];

  selected: CheckboxInput = new CheckboxInput(true);

  customReference: TextInput = new TextInput().withRule(new ValueRequiredRule()).withRule(new ValueMaxLength(20));
  customName: TextInput = new TextInput().withRule(new ValueMaxLength(30));

  constructor(
    private readonly cart: ShoppingCartData,
    private readonly cartItemContextActions: ICartItemContextAction[],
    private readonly cartItemGroupContextActions: ICartItemGroupContextAction[],
    private readonly itemQuantityChangeApproved: (item: ShoppingCartItemData, newQuantity: number) => Promise<void>,
    private readonly groupQuantityChangeApproved: (
      item: ShoppingCartItemGroupData,
      newQuantity: number,
    ) => Promise<void>,
    private readonly translation: CartItemListTranslation,
  ) {
    super();
    makeObservable(this);
    this.setCartItems(this.cart.items);
    this.setCartGroups(this.cart.groups);

    this.inputsToValidate.push(this.customReference);
    this.inputsToValidate.push(this.customName);
  }

  public get cartId() {
    return this.cart.id;
  }

  public get shoppingCart() {
    return this.cart;
  }

  public get preparingTimeInWorkingDays(): DaysRangeData | undefined {
    return this.cart.preparingTimeInWorkingDays;
  }

  public get numberOfItems() {
    return this.cart.items.sum((item) => item.quantity) + this.cart.groups.sum((group) => group.quantity);
  }

  @action.bound
  private setCartItems(cartItems: ShoppingCartItemData[]) {
    const newCartItems = cartItems.map((item) => {
      const oldItemState = this.cartItems.find((c) => c.item.id === item.id);
      const newItemState = new CartItemState(
        item,
        {
          contextActions: this.cartItemContextActions,
          changeQuantityAction: this.itemQuantityChangeApproved,
        },
        this.translation.cartItem,
      );
      if (oldItemState) {
        newItemState.setQuantityUpdate(oldItemState.quantityUpdate);
      }
      return newItemState;
    });
    this.cartItems = newCartItems;
  }

  @action.bound
  private setCartGroups(cartGroups: ShoppingCartItemGroupData[]) {
    const newCartGroups = cartGroups.map((group) => {
      const oldGroupState = this.cartGroups.find((c) => c.item.id === group.id);
      const newGroupState = new CartItemGroupState(
        group,
        {
          contextActions: this.cartItemGroupContextActions,
          changeQuantityAction: this.groupQuantityChangeApproved,
        },
        this.translation.cartItem,
      );
      if (oldGroupState) {
        newGroupState.setQuantityUpdate(oldGroupState.quantityUpdate);
      }
      return newGroupState;
    });
    this.cartGroups = newCartGroups;
  }
}
